import React, { useState, useEffect } from 'react';
import { Button, Details } from 'govuk-react-jsx';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import {
  sendClaimToDynamicsQueue,
  getAccount,
  getApplicationClaim,
  dateCalculation,
} from '../../firebase';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import ApplicationDetailsTable from './ApplicationDetailsTable';
import {
  getDocumentId,
  setSubmittedApplicationId,
} from '../../features/dashboard/dashboardSlice';

import useTitle from '../../hooks/useTitle';

const ViewConfirmInstallation = () => {
  const dispatch = useDispatch();
  const currentLanguage = useSelector(getCurrentLanguage);
  const documentId = useSelector(getDocumentId);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  useTitle(
    'dashboardHub.ViewConfirmInstallation.s15',
    'Review completed claim - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );
  const history = useHistory();
  const [reviewDeadline, setReviewDeadline] = useState('');
  const [dashboardLink, setDashboardLink] = useState('/dashboardHub/dashboard');
  const [challengeLink, setChallengeLink] = useState(
    '/landlordApp/challenge-submitted'
  );
  const [submitApprovalLink, setSubmitApprovalLink] = useState(
    '/landlordApp/approval-submitted'
  );
  const [clickedApproveBtn, setClickedApproveBtn] = useState(false);
  const [accountLoaded, setAccountLoaded] = useState(false);

  const handleApproveBtn = () => {
    if (!clickedApproveBtn) {
      setClickedApproveBtn(true);

      sendClaimToDynamicsQueue({ document_id: documentId })
        .then((response) => {
          if (response !== undefined || response !== null)
            history.push(submitApprovalLink);
        })
        .catch((error) => {
          if (error.code === 'functions/permission-denied') {
            window.location.assign(dashboardLink);
          }
        });
    }
  };
  const handleChallengeBtn = () => {
    history.push(challengeLink);
  };

  const sendDate = (e) => {
    // Calculate review deadline using working days and config value
    if (e) {
      dateCalculation({
        date: new Date(e).toISOString(),
        configDataPath: 'customer_challenge_period_days',
        useWorkingDays: true,
        format: 'DD MMMM YYYY',
      })
        .then((result) => {
          setReviewDeadline(result.data.calculatedDate);
        })
        .catch((error) => {
          console.error('Error calculating review deadline:', error);
          // Fallback to 7 calendar days if function fails
          setReviewDeadline(moment(e).add(7, 'days').format('DD MMMM YYYY'));
        });
    }
  };
  const sendCompanyName = () => {};

  useEffect(() => {
    dispatch(setSubmittedApplicationId(documentId));

    getAccount()
      .then((response) => {
        if (response.data?.account_type === 'Property Management Company') {
          setDashboardLink('/dashboardHub/dashboard-pmc');
          setChallengeLink('/pmcApp/challenge-installation');
          setSubmitApprovalLink('/pmcApp/approval-submitted');
        } else if (response.data?.account_type === 'Landlord') {
          setDashboardLink('/dashboardHub/dashboard');
          setChallengeLink('/landlordApp/challenge-installation');
          setSubmitApprovalLink('/landlordApp/approval-submitted');
        }
        setAccountLoaded(true);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.debug('Get Account error:', err.message);
        throw new Error(err);
      });
  });

  //  Avoids the user going back to the confirm installation page after approving to avoid reapproving
  useEffect(() => {
    if (accountLoaded) {
      getApplicationClaim({ id: documentId })
        .then((response) => {
          const { data } = response;
          if (data?.claim_status === 'Waiting Dynamics') {
            window.location.assign(dashboardLink);
          }
        })
        .catch((error) => {
          if (error.code === 'functions/permission-denied') {
            window.location.assign(dashboardLink);
          }
        });
    }
  }, [accountLoaded]);

  const pageModel = (
    <>
      <h1 className="govuk-heading-xl">
        {copyProvider.getCopy(
          'dashboardHub.ViewConfirmInstallation.s1',
          'Review completed claim'
        )}
      </h1>
      <p className="govuk-body">
        {copyProvider.getCopy(
          'dashboardHub.ViewConfirmInstallation.s2',
          'Your installer has completed the claim for this installation.'
        )}
      </p>

      <p className="govuk-body">
        {copyProvider.getCopy(
          'dashboardHub.ViewConfirmInstallation.s3',
          'You have until'
        )}
        <span>
          <strong> {reviewDeadline} </strong>
        </span>
        <span>
          {copyProvider.getCopy(
            'dashboardHub.ViewConfirmInstallation.s4',
            'to review the evidence provided. Approve the installation if you are happy the information your installer has submitted is correct.'
          )}
        </span>
      </p>

      <Details
        summaryChildren={copyProvider.getCopy(
          'dashboardHub.ViewConfirmInstallation.s5',
          'When should I challenge an installation?'
        )}
      >
        <p>
          {copyProvider.getCopy(
            'dashboardHub.ViewConfirmInstallation.s6',
            'If you notice a problem with the evidence or information provided by your installer, you should challenge the claim. For example:'
          )}
        </p>

        <ul>
          <li>
            {copyProvider.getCopy(
              'dashboardHub.ViewConfirmInstallation.s8',
              'the invoice is incorrect'
            )}
          </li>
          <li>
            {copyProvider.getCopy(
              'dashboardHub.ViewConfirmInstallation.s9',
              'the photos provided are wrong'
            )}
          </li>
          <li>
            {copyProvider.getCopy(
              'dashboardHub.ViewConfirmInstallation.s10',
              'the total amount does not match what was paid'
            )}
          </li>
          <li>
            {copyProvider.getCopy(
              'dashboardHub.ViewConfirmInstallation.s11',
              'the chargepoint information does not match what was installed'
            )}
          </li>
        </ul>
      </Details>
      <h1 className="govuk-heading-m">
        {copyProvider.getCopy(
          'dashboardHub.ViewConfirmInstallation.s12',
          'Claim summary'
        )}
      </h1>
      <ApplicationDetailsTable
        sendDate={sendDate}
        sendCompanyName={sendCompanyName}
        status="Awaiting customer review"
      />
      <div className="govuk-button-group">
        <Button
          className="govuk-button"
          onClick={handleApproveBtn}
          disabled={clickedApproveBtn}
        >
          {clickedApproveBtn
            ? copyProvider.getCopy(
                'dashboardHub.ViewConfirmInstallation.s13',
                'Approve installation'
              )
            : copyProvider.getCopy(
                'dashboardHub.ViewConfirmInstallation.s13',
                'Approve installation'
              )}
        </Button>
        <Button
          className="govuk-button--secondary"
          onClick={handleChallengeBtn}
        >
          {copyProvider.getCopy(
            'dashboardHub.ViewConfirmInstallation.s14',
            'Challenge installation'
          )}
        </Button>
      </div>
    </>
  );
  return pageModel;
};

export default ViewConfirmInstallation;
